"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { useNavigate, useSearchParams, useLocation } from "react-router-dom"
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, Polyline, useMap } from "react-leaflet"
import L from "leaflet"
import { getRoute } from "../../services/routeService"
import "leaflet/dist/leaflet.css"
import { format, parseISO, isWeekend } from "date-fns"
import { fr } from "date-fns/locale/fr"
import { api } from "../../services/api"
import { toast } from "react-toastify"
import {
  getEmptyClientDevisForm,
  validateDemenagementPhotoCounts,
  buildServiceRequestBody,
  buildStructuredQuoteDescription,
} from "./clientDevisFormDefaults"
import { DevisMobileAlignedFields } from "./DevisMobileReservationFields"
import { normalizeBusyDays, getBusyTimeConflictMessage } from "../../utils/moverBusyTimes"

const createCustomIcon = (color = "#F59E0B") =>
  new L.Icon({
    iconUrl: `data:image/svg+xml;base64,${btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="32" height="32">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `)}`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  })

const departIcon = createCustomIcon("#10B981")
const arriveeIcon = createCustomIcon("#EF4444")

const STEPS = [
  { id: 1, title: "Trajet", description: "Départ et arrivée", icon: "🗺️" },
  { id: 2, title: "Détails", description: "Volume et logement", icon: "🏠" },
  { id: 3, title: "Services", description: "Options et camion", icon: "🚛" },
  { id: 4, title: "Planning", description: "Date et horaires", icon: "📅" },
  { id: 5, title: "Récapitulatif", description: "Validation finale", icon: "✅" },
]

const SERVICE_TYPES = [
  {
    id: "demenagement",
    label: "Déménagement",
    icon: "🏠",
    description: "Déménagement complet de votre logement",
    features: ["Emballage professionnel", "Transport sécurisé", "Montage/Démontage"],
  },
  {
    id: "transport",
    label: "Transport et livraison",
    icon: "📦",
    description: "Transport de colis et objets",
    features: ["Livraison rapide", "Suivi en temps réel", "Emballage optionnel"],
  },
]

const TRUCK_TYPES = [
  {
    id: "small",
    label: "Petit utilitaire",
    capacity: "Jusqu'à 15m³",
    icon: "🚐",
    basePrice: 150,
    description: "Idéal pour studio/T1",
    features: ["1-2 déménageurs", "Assurance incluse", "Carburant inclus"],
  },
  {
    id: "medium",
    label: "Fourgon",
    capacity: "Jusqu'à 30m³",
    icon: "🚚",
    basePrice: 250,
    description: "Parfait pour T2/T3",
    features: ["2-3 déménageurs", "Assurance renforcée", "Équipement professionnel"],
  },
  {
    id: "large",
    label: "Grand fourgon",
    capacity: "Jusqu'à 50m³",
    icon: "🚛",
    basePrice: 350,
    description: "Adapté aux grandes maisons",
    features: ["3-4 déménageurs", "Assurance premium", "Matériel de protection"],
  },
  {
    id: "special",
    label: "Camion spécial",
    capacity: "Plus de 50m³",
    icon: "🚛🔧",
    basePrice: 500,
    description: "Pour déménagements complexes",
    features: ["Équipe spécialisée", "Matériel sur mesure", "Assurance tous risques"],
  },
]

const SERVICES_CONFIG = {
  emballage: {
    label: "Emballage professionnel",
    icon: "📦",
    desc: "Protection optimale de vos biens fragiles",
    price: 80,
    unit: "forfait",
  },
  montageMeubles: {
    label: "Montage/Démontage de meubles",
    icon: "🔧",
    desc: "Service complet d'assemblage par nos experts",
    price: 60,
    unit: "forfait",
  },
  nettoyage: {
    label: "Nettoyage après déménagement",
    icon: "🧹",
    desc: "Remise en état complète des locaux",
    price: 120,
    unit: "forfait",
  },
  cartons: {
    label: "Fourniture de cartons",
    icon: "📋",
    desc: "Kit complet de cartons professionnels",
    price: 45,
    unit: "kit",
  },
  gardeMeubles: {
    label: "Garde-meubles sécurisé",
    icon: "🏢",
    desc: "Stockage temporaire dans nos entrepôts",
    price: 25,
    unit: "m³/mois",
  },
  pianoService: {
    label: "Transport piano/objets lourds",
    icon: "🎹",
    desc: "Équipement spécialisé pour objets fragiles",
    price: 150,
    unit: "forfait",
  },
  assuranceExtra: {
    label: "Assurance complémentaire",
    icon: "🛡️",
    desc: "Couverture étendue jusqu'à 50 000€",
    price: 35,
    unit: "forfait",
  },
}

const MapClickHandler = ({ onClick, isSelecting }) => {
  useMapEvents({
    click: onClick,
  })
  return null
}

const ChatAssistant = ({ isOpen, onClose, onSendMessage }) => {
  const [message, setMessage] = useState("")
  const [messages, setMessages] = useState([
    { type: "bot", text: "Bonjour ! Je suis là pour vous aider avec votre devis. Avez-vous des questions ?" },
  ])

  const handleSend = () => {
    if (!message.trim()) return

    setMessages((prev) => [...prev, { type: "user", text: message }])
    setMessage("")

    // Simulation de réponse automatique
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          type: "bot",
          text: "Merci pour votre question ! Un de nos experts vous répondra dans quelques minutes.",
        },
      ])
    }, 1000)
  }

  if (!isOpen) return null

  return (
    <div className="fixed bottom-4 right-4 w-80 h-96 bg-gray-800/95 backdrop-blur-sm border border-gray-600 rounded-xl shadow-2xl z-50">
      <div className="flex items-center justify-between p-4 border-b border-gray-600">
        <h3 className="text-lg font-semibold text-amber-400">Assistant Liberty</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 p-4 h-64 overflow-y-auto space-y-3">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-xs p-3 rounded-lg text-sm ${
                msg.type === "user" ? "bg-amber-500 text-gray-900" : "bg-gray-700 text-gray-200"
              }`}
            >
              {msg.text}
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 border-t border-gray-600">
        <div className="flex space-x-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSend()}
            placeholder="Tapez votre message..."
            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none"
          />
          <button
            onClick={handleSend}
            className="px-4 py-2 bg-amber-500 text-gray-900 rounded-lg hover:bg-amber-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

const AutoSave = ({ formData, onSave }) => {
  const [lastSaved, setLastSaved] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const timer = setTimeout(async () => {
      setSaving(true)
      try {
        await onSave(formData)
        setLastSaved(new Date())
      } catch (error) {
        console.error("Erreur de sauvegarde:", error)
      } finally {
        setSaving(false)
      }
    }, 2000)

    return () => clearTimeout(timer)
  }, [formData, onSave])

  return (
    <div className="flex items-center text-xs text-gray-400 mb-4">
      {saving ? (
        <>
          <svg className="animate-spin w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Sauvegarde...
        </>
      ) : lastSaved ? (
        <>
          <svg className="w-3 h-3 mr-1 text-green-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          Sauvegardé à {format(lastSaved, "HH:mm")}
        </>
      ) : null}
    </div>
  )
}

function ClientDevis() {
  const [serviceTypeSelected, setServiceTypeSelected] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [mapKey, setMapKey] = useState(0)
  const [selectingOnMap, setSelectingOnMap] = useState(null)
  const [mapCenter, setMapCenter] = useState({ lat: 36.8065, lng: 10.1815 })
  const [mapZoom] = useState(12)
  const [routeCoords, setRouteCoords] = useState(null)
  const [routeInfo, setRouteInfo] = useState(null)
  const [chatOpen, setChatOpen] = useState(false)
  const [priceEstimate, setPriceEstimate] = useState(0)
  // eslint-disable-next-line no-unused-vars
  const [availableSlots, setAvailableSlots] = useState([]) // Utilisé pour les créneaux horaires (étape Planning)
  const [clientProfile, setClientProfile] = useState(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const preselectedMover = useMemo(() => {
    const id = searchParams.get("mover")
    if (!id) return null
    const st = location.state?.preselectedMover
    if (st && String(st.id) === id) {
      return {
        id: st.id,
        firstName: st.firstName ?? "",
        lastName: st.lastName ?? "",
        truckType: st.truckType ?? null,
      }
    }
    const mn = searchParams.get("moverName")
    if (mn) {
      const p = String(mn).trim().split(/\s+/)
      return {
        id,
        firstName: p[0] || "",
        lastName: p.slice(1).join(" ") || "",
        truckType: searchParams.get("moverTruck") || null,
      }
    }
    return { id, firstName: "", lastName: "", truckType: searchParams.get("moverTruck") || null }
  }, [searchParams, location.state])

  const [busyDays, setBusyDays] = useState({})

  useEffect(() => {
    let cancelled = false
    const moverId = preselectedMover?.id
    if (!moverId) {
      setBusyDays({})
      return undefined
    }
    ;(async () => {
      try {
        const json = await api.getMoverBusyDays(moverId)
        if (!cancelled && json?.success && json.busyDays && typeof json.busyDays === "object") {
          setBusyDays(normalizeBusyDays(json.busyDays))
        } else if (!cancelled) {
          setBusyDays({})
        }
      } catch {
        if (!cancelled) setBusyDays({})
      }
    })()
    return () => {
      cancelled = true
    }
  }, [preselectedMover?.id])

  const [form, setForm] = useState(() => getEmptyClientDevisForm())

  const calculateRealTimePrice = useCallback(() => {
    let basePrice = 0
    let servicesPrice = 0
    let distancePrice = 0
    let urgencyMultiplier = 1
    let difficultyMultiplier = 1

    const selectedTruck = TRUCK_TYPES.find((t) => t.id === form.truckType)
    if (selectedTruck) {
      basePrice = selectedTruck.basePrice
    }

    if (form.services && typeof form.services === 'object') {
      Object.entries(form.services).forEach(([key, selected]) => {
        if (selected && SERVICES_CONFIG[key]) {
          servicesPrice += SERVICES_CONFIG[key].price
        }
      })
    }

    if (routeInfo?.distance) {
      const km = routeInfo.distance / 1000
      distancePrice = km * 0.8
    }

    if (form.urgence) urgencyMultiplier = 1.3
    if (form.accessDifficulty === "difficult") difficultyMultiplier = 1.2
    if (form.accessDifficulty === "very_difficult") difficultyMultiplier = 1.5

    const flexibleDiscount = form.flexibleDate ? 0.9 : 1

    const totalPrice =
      (basePrice + servicesPrice + distancePrice) * urgencyMultiplier * difficultyMultiplier * flexibleDiscount

    return Math.round(totalPrice * 100) / 100
  }, [form, routeInfo])

  useEffect(() => {
    const newPrice = calculateRealTimePrice()
    setPriceEstimate(newPrice)
  }, [calculateRealTimePrice])

  const generateTimeSlots = (selectedDate) => {
    const slots = []
    const date = new Date(selectedDate)
    const isWeekendDay = isWeekend(date)

    // Créneaux différents selon weekend/semaine
    const startHour = isWeekendDay ? 9 : 8
    const endHour = isWeekendDay ? 17 : 18

    for (let hour = startHour; hour <= endHour; hour += 2) {
      const available = Math.random() > 0.3 // 70% de chance d'être disponible
      slots.push({
        time: `${hour.toString().padStart(2, "0")}:00`,
        available,
        price: available ? 0 : 25, // Supplément pour créneaux moins demandés
      })
    }

    return slots
  }

  useEffect(() => {
    if (form.date) {
      const slots = generateTimeSlots(form.date)
      setAvailableSlots(slots)
    }
  }, [form.date])

  // Mise à jour de la position de la carte via géolocalisation (si disponible)
  // Charger le profil du client au montage
  useEffect(() => {
    const loadClientProfile = async () => {
      try {
        const profile = await api.getClientProfile()
        setClientProfile(profile)
        
        // Pré-remplir les champs avec les données du client
        if (profile?.address) {
          setForm(prev => ({
            ...prev,
            depart: profile.address,
          }))
        }
      } catch (error) {
        console.error("Erreur lors du chargement du profil client:", error)
      }
    }
    
    loadClientProfile()
  }, [])

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setMapCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: true, timeout: 8000 },
      )
    }
  }, [])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    if (type === "checkbox") {
      if (name.startsWith("services.")) {
        const serviceName = name.split(".")[1]
        setForm((prev) => ({
          ...prev,
          services: {
            ...prev.services,
            [serviceName]: checked,
          },
        }))
      } else {
        setForm((prev) => ({ ...prev, [name]: checked }))
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }))

      if (name === "volume" && value) {
        const volume = Number.parseFloat(value)
        let suggestedTruck = ""

        if (volume <= 15) {
          suggestedTruck = "small"
        } else if (volume <= 30) {
          suggestedTruck = "medium"
        } else if (volume <= 50) {
          suggestedTruck = "large"
        } else {
          suggestedTruck = "xlarge"
        }

        setForm((prev) => ({ ...prev, truckType: suggestedTruck }))
      }
    }

    // Sauvegarde automatique
    const autoSave = true
    if (autoSave) {
      const updatedForm = { ...form, [name]: type === "checkbox" ? checked : value }
      saveQuoteDraft(updatedForm)
    }
  }

  const validateStep = (step) => {
    const newErrors = {}

    if (step === 1) {
      if (!form.depart?.trim()) newErrors.depart = "Veuillez indiquer le lieu de départ"
      if (!form.arrivee?.trim()) newErrors.arrivee = "Veuillez indiquer le lieu d'arrivée"
      if (form.depart === form.arrivee) newErrors.arrivee = "Le lieu d'arrivée doit être différent du départ"
    } else if (step === 2) {
      if (!form.volume) newErrors.volume = "Veuillez estimer le volume"
      if (form.volume && (form.volume < 1 || form.volume > 200)) {
        newErrors.volume = "Le volume doit être entre 1 et 200 m³"
      }
      // Validation spécifique selon le type de service
      if (form.serviceType === "demenagement" && form.typeLogement === "appartement" && !form.etage) {
        newErrors.etage = "Veuillez indiquer l'étage"
      }
    } else if (step === 3) {
      if (!form.truckType) newErrors.truckType = "Veuillez sélectionner un type de camion"
    } else if (step === 4) {
      if (!form.date) newErrors.date = "Veuillez sélectionner une date"
      if (!form.timeSlot) newErrors.timeSlot = "Veuillez choisir un créneau horaire"

      // Vérifier que la date n'est pas dans le passé
      const selectedDate = new Date(form.date)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (selectedDate < today) {
        newErrors.date = "La date ne peut pas être dans le passé"
      }
    }
    // No validation needed for step 5 (summary/final step)

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = () => {
    if (currentStep === STEPS.length) {
      // We're on the final step, allow submission
      return
    }

    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length))
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1))
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const saveQuoteDraft = async (formData) => {
    try {
      const draftData = {
        ...formData,
        timestamp: new Date().toISOString(),
        step: currentStep,
      }
      localStorage.setItem("client_quote_draft", JSON.stringify(draftData))
    } catch (error) {
      console.error("Erreur de sauvegarde:", error)
    }
  }

  useEffect(() => {
    const savedDraft = localStorage.getItem("client_quote_draft")
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft)
        const draftAge = Date.now() - new Date(draftData.timestamp).getTime()

        // Charger le brouillon s'il a moins de 24h
        if (draftAge < 24 * 60 * 60 * 1000) {
          // S'assurer que tous les champs nécessaires sont présents
          const defaultForm = getEmptyClientDevisForm()
          const mergedForm = {
            ...defaultForm,
            ...draftData,
            // S'assurer que colis est toujours un objet valide
            colis: draftData.colis && typeof draftData.colis === "object" ? draftData.colis : defaultForm.colis,
            services: draftData.services && typeof draftData.services === "object" ? draftData.services : defaultForm.services,
            coordonnees: draftData.coordonnees && typeof draftData.coordonnees === "object" ? draftData.coordonnees : defaultForm.coordonnees,
            cuisine: { ...defaultForm.cuisine, ...(draftData.cuisine || {}) },
            cuisinePhotos: { ...defaultForm.cuisinePhotos, ...(draftData.cuisinePhotos || {}) },
            chambres:
              Array.isArray(draftData.chambres) && draftData.chambres.length > 0 ? draftData.chambres : defaultForm.chambres,
          }
          setForm(mergedForm)
          setCurrentStep(draftData.step || 1)
        }
      } catch (error) {
        console.error("Erreur de chargement du brouillon:", error)
      }
    }
  }, [])

  // Gestion de la sélection sur la carte
  const handleMapClick = (e) => {
    if (!selectingOnMap) return

    const { lat, lng } = e.latlng
    const newCoords = { lat, lng }

    setForm((prev) => ({
      ...prev,
      coordonnees: {
        ...prev.coordonnees,
        [selectingOnMap]: newCoords,
      },
    }))

    updateAddressFromCoords(selectingOnMap, newCoords)
    setSelectingOnMap(null)
  }

  // Récupérer l'itinéraire via notre API backend quand départ et arrivée sont définis
  useEffect(() => {
    const fetchRoute = async () => {
      const start = form.coordonnees.depart
      const end = form.coordonnees.arrivee

      if (!start || !end) {
        setRouteCoords(null)
        setRouteInfo(null)
        return
      }

      try {
        const routeData = await getRoute(start, end)
        const firstRoute = routeData.routes?.[0]

        if (firstRoute?.geometry?.coordinates) {
          // Convertir les coordonnées [lng, lat] en [lat, lng] pour Leaflet
          const coords = firstRoute.geometry.coordinates.map(([lng, lat]) => [lat, lng])
          setRouteCoords(coords)
          setRouteInfo({
            distance: firstRoute.distance,
            duration: firstRoute.duration,
          })
        }
      } catch (error) {
        console.error("Erreur lors de la récupération de l'itinéraire:", error)
        // Gérer l'erreur (afficher un message à l'utilisateur, etc.)
      }
    }

    fetchRoute()
  }, [form.coordonnees.depart, form.coordonnees.arrivee])

  // Ajuster la vue sur l'itinéraire
  const FitBoundsOnRoute = ({ coords }) => {
    const map = useMap()
    useEffect(() => {
      if (coords && coords.length > 1) {
        map.fitBounds(coords)
      }
    }, [coords, map])
    return null
  }

  // Mise à jour de l'adresse à partir des coordonnées (géocodage inversé)
  const updateAddressFromCoords = async (type, coords) => {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.lat}&lon=${coords.lng}`
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      })
      const data = await response.json()
      const address = data?.display_name || `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`
      setForm((prev) => ({
        ...prev,
        [type]: address,
      }))
    } catch (error) {
      console.error("Erreur lors du géocodage inversé:", error)
      setForm((prev) => ({
        ...prev,
        [type]: `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`,
      }))
    }
  }

  const devisBusyTimeHint = useMemo(() => {
    if (!preselectedMover?.id) return null
    return getBusyTimeConflictMessage(form.date, form.heure || "09:00", busyDays)
  }, [preselectedMover?.id, form.date, form.heure, busyDays])

  const belowAddressesMapSlot = (
    <div className="rounded-xl overflow-hidden border border-gray-600 shadow-lg relative h-72 md:h-96">
      <MapContainer key={mapKey} center={mapCenter} zoom={mapZoom} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <MapClickHandler onClick={handleMapClick} isSelecting={selectingOnMap} />
        {form.coordonnees?.depart && (
          <Marker position={form.coordonnees.depart} icon={departIcon}>
            <Popup>
              <div className="text-center">
                <strong>Départ</strong>
                <br />
                {form.depart}
              </div>
            </Popup>
          </Marker>
        )}
        {form.coordonnees?.arrivee && (
          <Marker position={form.coordonnees.arrivee} icon={arriveeIcon}>
            <Popup>
              <div className="text-center">
                <strong>Arrivée</strong>
                <br />
                {form.arrivee}
              </div>
            </Popup>
          </Marker>
        )}
        {routeCoords && (
          <>
            <Polyline positions={routeCoords} color="#F59E0B" weight={5} opacity={0.8} dashArray="10, 10" />
            <FitBoundsOnRoute coords={routeCoords} />
          </>
        )}
      </MapContainer>
      {selectingOnMap && (
        <div className="absolute top-3 left-3 right-3 bg-amber-900/90 border border-amber-700 p-3 rounded-lg backdrop-blur-sm z-[1000] text-sm">
          <p className="text-amber-100 font-medium">
            Cliquez sur la carte pour positionner le point de {selectingOnMap === "depart" ? "départ" : "d'arrivée"}
          </p>
        </div>
      )}
    </div>
  )

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Si c'est le formulaire de transport
    if (form.serviceType === "transport") {
      const newErrors = {}

      if (!form.articleTransport?.trim()) {
        newErrors.articleTransport = "Veuillez décrire l’article à transporter"
      }
      if (!form.depart?.trim()) newErrors.depart = "Veuillez indiquer l'adresse de départ"
      if (!form.arrivee?.trim()) newErrors.arrivee = "Veuillez indiquer l'adresse d'arrivée"
      if (!form.date) newErrors.date = "Veuillez sélectionner une date"
      if (!form.heure?.trim()) newErrors.heure = "Veuillez indiquer une heure"
      if (
        form.moverServiceMode === "avec_travailleurs" &&
        form.propertyType === "appartement" &&
        !form.floor?.trim()
      ) {
        newErrors.floor = "Veuillez indiquer l'étage"
      }

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (form.date && new Date(form.date) < today) {
        newErrors.date = "La date ne peut pas être dans le passé"
      }

      if (preselectedMover?.id && Object.keys(busyDays).length > 0) {
        const busyMsg = getBusyTimeConflictMessage(form.date, form.heure || "09:00", busyDays)
        if (busyMsg) newErrors.heure = busyMsg
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        return
      }

      setErrors({})
      setLoading(true)
      try {
        const localUser = JSON.parse(localStorage.getItem("user") || "{}")
        const clientName =
          `${localUser.firstName || localUser.first_name || ""} ${localUser.lastName || localUser.last_name || ""}`.trim() ||
          "Client"
        const clientEmail = localUser.email || clientProfile?.email || ""
        const moverId = preselectedMover?.id

        if (moverId) {
          const body = buildServiceRequestBody({ ...form, serviceType: "transport" }, moverId)
          const response = await api.createServiceRequest(body)
          if (response && response.success) {
            toast.success("Votre demande de transport a été envoyée au déménageur.")
            setServiceTypeSelected(false)
            setForm(getEmptyClientDevisForm())
            navigate("/client/quotes")
          } else {
            throw new Error(response?.message || "Erreur lors de l'envoi de la demande")
          }
        } else {
          const quoteData = {
            client_id: localUser.id || clientProfile?.id,
            nom: clientName,
            email: clientEmail,
            service_type: "transport",
            depart: form.depart,
            arrivee: form.arrivee,
            date: form.date,
            from_address: form.depart,
            to_address: form.arrivee,
            move_date: form.date,
            description: buildStructuredQuoteDescription({ ...form, serviceType: "transport" }),
          }
          const response = await api.createQuote(quoteData)
          if (response && response.success) {
            toast.success("Votre demande de transport a été enregistrée avec succès !")
            setServiceTypeSelected(false)
            setForm(getEmptyClientDevisForm())
            navigate("/client/quotes")
          } else {
            throw new Error(response?.message || "Erreur lors de la création du devis")
          }
        }
      } catch (error) {
        console.error("Erreur lors de la soumission:", error)
        setErrors({ submit: error.message || "Une erreur est survenue lors de l'envoi" })
        toast.error(error.message || "Erreur lors de l'envoi de la demande")
      } finally {
        setLoading(false)
      }
      return
    }

    // Si c'est le formulaire de déménagement
    if (form.serviceType === "demenagement") {
      const newErrors = {}

      if (!form.depart?.trim()) newErrors.depart = "Veuillez indiquer l'adresse de départ"
      if (!form.arrivee?.trim()) newErrors.arrivee = "Veuillez indiquer l'adresse d'arrivée"
      if (!form.date) newErrors.date = "Veuillez sélectionner une date"
      if (!form.heure?.trim()) newErrors.heure = "Veuillez indiquer une heure"
      if (
        form.moverServiceMode === "avec_travailleurs" &&
        form.propertyType === "appartement" &&
        !form.floor?.trim()
      ) {
        newErrors.floor = "Veuillez indiquer l'étage"
      }

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (form.date && new Date(form.date) < today) {
        newErrors.date = "La date ne peut pas être dans le passé"
      }

      const photoErr = validateDemenagementPhotoCounts(form)
      if (photoErr) {
        newErrors.photos = photoErr
      }

      if (preselectedMover?.id && Object.keys(busyDays).length > 0) {
        const busyMsg = getBusyTimeConflictMessage(form.date, form.heure || "09:00", busyDays)
        if (busyMsg) newErrors.heure = busyMsg
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        if (photoErr) toast.error(photoErr)
        return
      }

      setErrors({})
      setLoading(true)
      try {
        const localUser = JSON.parse(localStorage.getItem("user") || "{}")
        const clientName =
          `${localUser.firstName || localUser.first_name || ""} ${localUser.lastName || localUser.last_name || ""}`.trim() ||
          "Client"
        const clientEmail = localUser.email || clientProfile?.email || ""
        const moverId = preselectedMover?.id

        if (moverId) {
          const body = buildServiceRequestBody({ ...form, serviceType: "demenagement" }, moverId)
          const response = await api.createServiceRequest(body)
          if (response && response.success) {
            toast.success("Votre demande de déménagement a été envoyée au déménageur.")
            setServiceTypeSelected(false)
            setForm(getEmptyClientDevisForm())
            navigate("/client/quotes")
          } else {
            throw new Error(response?.message || "Erreur lors de l'envoi de la demande")
          }
        } else {
          const demDate = form.date || new Date().toISOString().slice(0, 10)
          const quoteData = {
            client_id: localUser.id || clientProfile?.id,
            nom: clientName,
            email: clientEmail,
            service_type: "demenagement",
            description: buildStructuredQuoteDescription({ ...form, serviceType: "demenagement" }),
            type_logement: form.moverServiceMode === "avec_travailleurs" ? form.propertyType : "maison",
            etage:
              form.moverServiceMode === "avec_travailleurs" && form.propertyType === "appartement"
                ? form.floor
                : null,
            ascenseur:
              form.moverServiceMode === "avec_travailleurs" && form.propertyType === "appartement"
                ? form.hasElevator
                : false,
            depart: form.depart,
            arrivee: form.arrivee,
            date: demDate,
            from_address: form.depart,
            to_address: form.arrivee,
            move_date: demDate,
            services: form.services || {},
          }

          const response = await api.createQuote(quoteData)
          if (response && response.success) {
            toast.success("Votre demande de déménagement a été enregistrée avec succès !")
            setServiceTypeSelected(false)
            setForm(getEmptyClientDevisForm())
            navigate("/client/quotes")
          } else {
            throw new Error(response?.message || "Erreur lors de la création du devis")
          }
        }
      } catch (error) {
        console.error("Erreur lors de la soumission:", error)
        setErrors({ submit: error.message || "Une erreur est survenue lors de l'envoi" })
        toast.error(error.message || "Erreur lors de l'envoi de la demande")
      } finally {
        setLoading(false)
      }
      return
    }

    // Ancien code pour le formulaire avec étapes (ne devrait plus être utilisé)
    if (currentStep < STEPS.length) {
      nextStep()
      return
    }

    if (!validateStep(currentStep)) return

    setLoading(true)

    try {
      const localUser = (() => {
        try {
          return JSON.parse(localStorage.getItem("user") || "{}")
        } catch {
          return {}
        }
      })()

      const quoteData = {
        ...form,
        client_id: localUser.id,
        nom: `${localUser.firstName || localUser.first_name || ""} ${localUser.lastName || localUser.last_name || ""}`.trim() || "Client",
        email: localUser.email || clientProfile?.email || "client@example.com",
        telephone: localUser.phone || clientProfile?.phone || "",
        estimatedPrice: priceEstimate,
        routeInfo,
        timestamp: new Date().toISOString(),
      }

      const response = await api.createQuote(quoteData)

      // Supprimer le brouillon après soumission réussie
      localStorage.removeItem("client_quote_draft")

      toast.success("Votre demande de devis a été enregistrée avec succès !")

      navigate("/client/quotes", {
        state: {
          success: "Votre demande de devis a été enregistrée avec succès !",
          newQuote: response,
          estimatedPrice: priceEstimate,
        },
      })
    } catch (error) {
      console.error("Erreur lors de la soumission du devis:", error)
      toast.error(error.response?.data?.message || "Une erreur est survenue lors de la soumission. Veuillez réessayer.")
      setErrors((prev) => ({
        ...prev,
        submit: error.response?.data?.message || "Une erreur est survenue lors de la soumission. Veuillez réessayer.",
      }))
    } finally {
      setLoading(false)
    }
  }

  // Fonction pour formater les dates
  const formatDate = (dateString) => {
    if (!dateString) return ""
    try {
      return format(parseISO(dateString), "PPP", { locale: fr })
    } catch (e) {
      return dateString
    }
  }

  // Fonction pour formater les montants en TND
  const formatTND = (amount) => {
    if (!amount && amount !== 0) return "À déterminer"
    return new Intl.NumberFormat("fr-TN", {
      style: "currency",
      currency: "TND",
      minimumFractionDigits: 3,
    }).format(amount)
  }

  // Calcul du prix estimé (exemple simplifié)
  const calculateEstimate = () => {
    // Logique de calcul du prix à implémenter
    // Pour l'instant, on retourne un montant factice
    return 450.5 // Exemple de montant en TND
  }

  // Effet pour forcer le rechargement de la carte quand on change de mode de sélection
  useEffect(() => {
    setMapKey((prev) => prev + 1)
  }, [selectingOnMap])

  // Si le type de service n'est pas encore sélectionné, afficher la page de sélection
  if (!serviceTypeSelected) {
    const moverLabel = [preselectedMover?.firstName, preselectedMover?.lastName].filter(Boolean).join(" ").trim()
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-white mb-2">Choisir votre service</h1>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              Complétez le formulaire ci-dessous ; votre demande sera envoyée au déménageur choisi lors de la recherche.
            </p>
            {preselectedMover && (
              <div className="mt-5 inline-flex flex-wrap items-center justify-center gap-2 px-4 py-3 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-100">
                <span className="text-gray-300">Déménageur sélectionné :</span>
                <span className="font-semibold text-white">
                  {moverLabel || "Profil lié à votre recherche"}
                </span>
              </div>
            )}
          </div>

          {/* Sélection du type de service */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {SERVICE_TYPES.map((service) => (
              <button
                key={service.id}
                onClick={() => {
                  setForm((prev) => ({
                    ...getEmptyClientDevisForm(),
                    serviceType: service.id,
                    depart: prev.depart || "",
                    coordonnees: prev.coordonnees || { depart: null, arrivee: null },
                  }))
                  setServiceTypeSelected(true)
                }}
                className="group relative p-10 border-2 rounded-2xl cursor-pointer transition-all duration-300 transform hover:scale-105 bg-gray-800/50 backdrop-blur-sm border-gray-600 hover:border-amber-500 hover:bg-amber-500/10 shadow-xl hover:shadow-2xl hover:shadow-amber-500/25"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="text-7xl mb-6 transform group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-3xl font-bold mb-3 text-white group-hover:text-amber-400 transition-colors">
                    {service.label}
                  </h3>
                  <p className="text-gray-400 mb-6 text-lg">{service.description}</p>
                  <div className="w-full space-y-3">
                    {service.features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-center text-sm text-gray-300"
                      >
                        <svg
                          className="w-5 h-5 mr-2 text-amber-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {feature}
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold rounded-xl opacity-0 group-hover:opacity-100 transition-opacity">
                    Choisir ce service
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Si le service sélectionné est "demenagement", afficher le formulaire de déménagement
  if (form.serviceType === "demenagement") {
    const moverFormLabel = [preselectedMover?.firstName, preselectedMover?.lastName].filter(Boolean).join(" ").trim()
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {preselectedMover && (
            <div className="mb-4 px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-100">
              Devis adressé à : <strong className="text-white">{moverFormLabel || "Déménageur sélectionné"}</strong>
            </div>
          )}
          {/* Header avec bouton retour */}
          <div className="mb-6">
            <button
              onClick={() => {
                setServiceTypeSelected(false)
                setForm(getEmptyClientDevisForm())
              }}
              className="flex items-center text-gray-400 hover:text-amber-400 transition-colors mb-4 group text-sm"
            >
              <svg className="w-4 h-4 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Retour
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-gray-800/60 backdrop-blur-xl shadow-xl rounded-2xl p-6 md:p-8 border border-gray-700/50 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-yellow-500/5 pointer-events-none"></div>
            
            <div className="relative z-10 space-y-6">
              {errors.submit && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm">
                  <p className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-2-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.submit}
                  </p>
                </div>
              )}

              {errors.photos && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-lg text-sm">{errors.photos}</div>
              )}

              <DevisMobileAlignedFields
                variant="demenagement"
                form={form}
                setForm={setForm}
                errors={errors}
                belowAddressesSlot={belowAddressesMapSlot}
                onMapPickRequest={setSelectingOnMap}
                selectingOnMap={selectingOnMap}
                busyTimeHint={devisBusyTimeHint}
                supplementalSlot={
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center mr-3">
                        <span className="text-lg">🛠️</span>
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-white">Services supplémentaires</h2>
                        <p className="text-gray-400 text-xs">Sélectionnez les services souhaités</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {[
                        { key: "emballage", label: "Emballage professionnel", icon: "📦" },
                        { key: "montage", label: "Montage/Démontage", icon: "🔧" },
                        { key: "garde_meuble", label: "Garde-meubles", icon: "🏢" },
                        { key: "cartons", label: "Fourniture de cartons", icon: "📋" },
                      ].map((service) => (
                        <label
                          key={service.key}
                          className="group flex items-center p-3 bg-gray-800/40 hover:bg-gray-800/60 rounded-lg border-2 border-gray-700 hover:border-amber-500/50 cursor-pointer transition-all"
                        >
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={form.services?.[service.key] || false}
                              onChange={(e) => {
                                setForm((prev) => ({
                                  ...prev,
                                  services: {
                                    ...(prev.services || {}),
                                    [service.key]: e.target.checked,
                                  },
                                }))
                              }}
                              className="sr-only"
                            />
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                form.services?.[service.key]
                                  ? "bg-gradient-to-r from-amber-500 to-yellow-500 border-amber-500"
                                  : "bg-gray-700/50 border-gray-600 group-hover:border-amber-500/50"
                              }`}
                            >
                              {form.services?.[service.key] && (
                                <svg className="w-3 h-3 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                          <div className="ml-3 flex items-center">
                            <span className="text-base mr-2">{service.icon}</span>
                            <span className="text-gray-300 text-sm font-medium">{service.label}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                }
              />

              {/* Bouton de soumission */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-700/50">
                <button
                  type="button"
                  onClick={() => {
                    setServiceTypeSelected(false)
                    setForm(getEmptyClientDevisForm())
                  }}
                  className="px-6 py-2.5 bg-gray-700/50 hover:bg-gray-700 text-white font-medium rounded-lg transition-all text-sm border border-gray-600 hover:border-gray-500"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-gray-900 font-bold rounded-lg transition-all duration-200 transform hover:scale-105 hover:shadow-lg hover:shadow-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2 text-sm"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Envoi...</span>
                    </>
                  ) : (
                    <>
                      <span>Envoyer la demande</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Si le service sélectionné est "transport", afficher le formulaire de transport
  if (form.serviceType === "transport") {
    const moverFormLabelT = [preselectedMover?.firstName, preselectedMover?.lastName].filter(Boolean).join(" ").trim()
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {preselectedMover && (
            <div className="mb-4 px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-100">
              Devis adressé à : <strong className="text-white">{moverFormLabelT || "Déménageur sélectionné"}</strong>
            </div>
          )}
          {/* Header avec bouton retour */}
          <div className="mb-6">
            <button
              onClick={() => {
                setServiceTypeSelected(false)
                setForm(getEmptyClientDevisForm())
              }}
              className="flex items-center text-gray-400 hover:text-amber-400 transition-colors mb-4 group text-sm"
            >
              <svg className="w-4 h-4 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Retour
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-gray-800/60 backdrop-blur-xl shadow-xl rounded-2xl p-6 md:p-8 border border-gray-700/50 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-yellow-500/5 pointer-events-none"></div>
            
            <div className="relative z-10 space-y-6">

              {errors.submit && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm">
                  <p className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-2-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.submit}
                  </p>
                </div>
              )}

              <DevisMobileAlignedFields
                variant="transport"
                form={form}
                setForm={setForm}
                errors={errors}
                belowAddressesSlot={belowAddressesMapSlot}
                onMapPickRequest={setSelectingOnMap}
                selectingOnMap={selectingOnMap}
                busyTimeHint={devisBusyTimeHint}
              />

              {/* Bouton de soumission */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-700/50">
                <button
                  type="button"
                  onClick={() => {
                    setServiceTypeSelected(false)
                    setForm(getEmptyClientDevisForm())
                  }}
                  className="px-6 py-2.5 bg-gray-700/50 hover:bg-gray-700 text-white font-medium rounded-lg transition-all text-sm border border-gray-600 hover:border-gray-500"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-gray-900 font-bold rounded-lg transition-all duration-200 transform hover:scale-105 hover:shadow-lg hover:shadow-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2 text-sm"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Envoi...</span>
                    </>
                  ) : (
                    <>
                      <span>Envoyer la demande</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">

        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex flex-col items-center relative group">
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center text-lg font-bold transition-all duration-500 transform ${
                    currentStep >= step.id
                      ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 shadow-lg shadow-amber-500/25 scale-110"
                      : currentStep === step.id - 1
                        ? "bg-gray-600 text-gray-300 border-2 border-amber-400 animate-pulse"
                        : "bg-gray-700 text-gray-400 border-2 border-gray-600"
                  }`}
                >
                  {currentStep > step.id ? (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <span className="text-2xl">{step.icon}</span>
                  )}
                </div>
                <div className="mt-3 text-center">
                  <span
                    className={`text-sm font-semibold transition-colors duration-300 ${
                      currentStep >= step.id ? "text-amber-400" : "text-gray-500"
                    }`}
                  >
                    {step.title}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">{step.description}</p>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`absolute top-8 left-16 h-0.5 transition-all duration-500 ${
                      currentStep > step.id
                        ? "bg-gradient-to-r from-amber-400 to-yellow-500 shadow-lg shadow-amber-500/25"
                        : "bg-gray-700"
                    }`}
                    style={{ width: "calc(100vw / 5 - 4rem)" }}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="h-3 bg-gray-800 rounded-full overflow-hidden shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 transition-all duration-700 shadow-lg relative"
              style={{ width: `${(currentStep / (STEPS.length - 1)) * 100}%` }}
            >
              <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>Début</span>
            <span className="text-amber-400 font-medium">
              {Math.round(((currentStep - 1) / (STEPS.length - 1)) * 100)}% complété
            </span>
            <span>Fin</span>
          </div>
        </div>

        <div className="flex items-center justify-between mb-8">
          <AutoSave formData={form} onSave={saveQuoteDraft} />

          {priceEstimate > 0 && (
            <div className="bg-gradient-to-r from-amber-900/20 to-yellow-900/20 px-6 py-3 rounded-xl border border-amber-700/50 backdrop-blur-sm">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  />
                </svg>
                <span className="text-sm text-gray-300">Estimation actuelle: </span>
                <span className="text-lg font-bold text-amber-400 ml-2">{formatTND(priceEstimate)}</span>
              </div>
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-gray-800/50 backdrop-blur-sm shadow-2xl rounded-3xl p-8 border border-gray-700 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-yellow-500/5 pointer-events-none"></div>

          {errors.submit && (
            <div className="mb-8 p-6 bg-red-900/50 border border-red-700 text-red-300 rounded-xl backdrop-blur-sm relative">
              <div className="flex items-start">
                <svg className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-2-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <h3 className="font-semibold mb-1">Erreur de soumission</h3>
                  <p>{errors.submit}</p>
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-10 relative">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-white mb-3">🗺️ Définissez votre trajet</h2>
                <p className="text-gray-400 text-lg">
                  Indiquez vos points de départ et d'arrivée pour calculer la distance
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="relative">
                    <label htmlFor="depart" className="block text-sm font-semibold text-gray-300 mb-3">
                      📍 Lieu de départ *
                    </label>
                    <div className="relative group">
                      <input
                        type="text"
                        id="depart"
                        name="depart"
                        value={form.depart}
                        onChange={handleChange}
                        className={`block w-full px-5 py-4 bg-gray-700/50 border-2 rounded-xl text-white placeholder-gray-400 backdrop-blur-sm transition-all duration-300 ${
                          errors.depart
                            ? "border-red-500 focus:border-red-400 focus:ring-red-400/20"
                            : "border-gray-600 focus:border-amber-500 focus:ring-amber-500/20 group-hover:border-amber-400"
                        } focus:ring-4 focus:outline-none text-lg`}
                        placeholder="Saisissez l'adresse de départ"
                      />
                      <button
                        type="button"
                        onClick={() => setSelectingOnMap("depart")}
                        className={`absolute inset-y-0 right-0 px-4 flex items-center transition-colors ${
                          selectingOnMap === "depart"
                            ? "text-green-400 animate-pulse"
                            : "text-amber-400 hover:text-amber-300"
                        }`}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </button>
                    </div>
                    {errors.depart && (
                      <p className="mt-3 text-sm text-red-400 flex items-center animate-fade-in">
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-2-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {errors.depart}
                      </p>
                    )}
                  </div>

                  <div className="relative">
                    <label htmlFor="arrivee" className="block text-sm font-semibold text-gray-300 mb-3">
                      🎯 Lieu d'arrivée *
                    </label>
                    <div className="relative group">
                      <input
                        type="text"
                        id="arrivee"
                        name="arrivee"
                        value={form.arrivee}
                        onChange={handleChange}
                        className={`block w-full px-5 py-4 bg-gray-700/50 border-2 rounded-xl text-white placeholder-gray-400 backdrop-blur-sm transition-all duration-300 ${
                          errors.arrivee
                            ? "border-red-500 focus:border-red-400 focus:ring-red-400/20"
                            : "border-gray-600 focus:border-amber-500 focus:ring-amber-500/20 group-hover:border-amber-400"
                        } focus:ring-4 focus:outline-none text-lg`}
                        placeholder="Saisissez l'adresse d'arrivée"
                      />
                      <button
                        type="button"
                        onClick={() => setSelectingOnMap("arrivee")}
                        className={`absolute inset-y-0 right-0 px-4 flex items-center transition-colors ${
                          selectingOnMap === "arrivee"
                            ? "text-red-400 animate-pulse"
                            : "text-amber-400 hover:text-amber-300"
                        }`}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </button>
                    </div>
                    {errors.arrivee && (
                      <p className="mt-3 text-sm text-red-400 flex items-center animate-fade-in">
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-2-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {errors.arrivee}
                      </p>
                    )}
                  </div>

                  {routeInfo && (
                    <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-700/50 p-6 rounded-xl backdrop-blur-sm">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-semibold text-green-300 mb-2">Itinéraire calculé</h3>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-400">Distance:</span>
                              <span className="text-green-300 font-semibold ml-2">
                                {(routeInfo.distance / 1000).toFixed(1)} km
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-400">Durée:</span>
                              <span className="text-green-300 font-semibold ml-2">
                                {Math.round(routeInfo.duration / 60)} min
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative">
                  <div className="h-96 rounded-2xl overflow-hidden border-2 border-gray-600 shadow-2xl relative">
                    <MapContainer
                      key={mapKey}
                      center={mapCenter}
                      zoom={mapZoom}
                      style={{ height: "100%", width: "100%" }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <MapClickHandler onClick={handleMapClick} isSelecting={selectingOnMap} />
                      {form.coordonnees.depart && (
                        <Marker position={form.coordonnees.depart} icon={departIcon}>
                          <Popup>
                            <div className="text-center">
                              <strong>Point de départ</strong>
                              <br />
                              {form.depart}
                            </div>
                          </Popup>
                        </Marker>
                      )}
                      {form.coordonnees.arrivee && (
                        <Marker position={form.coordonnees.arrivee} icon={arriveeIcon}>
                          <Popup>
                            <div className="text-center">
                              <strong>Point d'arrivée</strong>
                              <br />
                              {form.arrivee}
                            </div>
                          </Popup>
                        </Marker>
                      )}
                      {routeCoords && (
                        <>
                          <Polyline
                            positions={routeCoords}
                            color="#F59E0B"
                            weight={5}
                            opacity={0.8}
                            dashArray="10, 10"
                          />
                          <FitBoundsOnRoute coords={routeCoords} />
                        </>
                      )}
                    </MapContainer>

                    {selectingOnMap && (
                      <div className="absolute top-4 left-4 right-4 bg-amber-900/90 border border-amber-700 p-4 rounded-xl backdrop-blur-sm z-[1000]">
                        <div className="flex items-center">
                          <svg
                            className="w-6 h-6 mr-3 text-amber-300 animate-bounce"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <div>
                            <p className="text-amber-300 font-semibold">
                              Cliquez sur la carte pour positionner le point de{" "}
                              {selectingOnMap === "depart" ? "départ" : "d'arrivée"}
                            </p>
                            <p className="text-amber-200 text-sm mt-1">
                              Ou annulez en cliquant à nouveau sur le bouton de localisation
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Étape 2: Détails */}
          {currentStep === 2 && (
            <div className="space-y-8">
              <h2 className="text-2xl font-bold text-white mb-2">Détails du déménagement</h2>
              <p className="text-gray-400 mb-6">Précisez les caractéristiques de votre logement</p>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-4">Type de logement *</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {["maison", "appartement", "bureau", "autre"].map((type) => (
                    <label
                      key={type}
                      className={`relative p-4 border rounded-xl cursor-pointer transition-all duration-200 ${
                        form.typeLogement === type
                          ? "border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/20"
                          : "border-gray-600 bg-gray-700/30 hover:border-amber-400 hover:bg-amber-500/5"
                      } backdrop-blur-sm`}
                    >
                      <input
                        type="radio"
                        name="typeLogement"
                        value={type}
                        checked={form.typeLogement === type}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className="flex items-center">
                        <div
                          className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                            form.typeLogement === type ? "border-amber-500 bg-amber-500" : "border-gray-500"
                          }`}
                        >
                          {form.typeLogement === type && <div className="w-2 h-2 rounded-full bg-white"></div>}
                        </div>
                        <span
                          className={`block text-sm font-medium capitalize ${
                            form.typeLogement === type ? "text-amber-400" : "text-gray-300"
                          }`}
                        >
                          {type}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {form.typeLogement === "appartement" && (
                  <div>
                    <label htmlFor="etage" className="block text-sm font-medium text-gray-300 mb-2">
                      Étage *
                    </label>
                    <select
                      id="etage"
                      name="etage"
                      value={form.etage}
                      onChange={handleChange}
                      className={`block w-full px-4 py-3 bg-gray-700/50 border rounded-lg text-white backdrop-blur-sm transition-all duration-200 ${
                        errors.etage
                          ? "border-red-500 focus:border-red-400 focus:ring-red-400/20"
                          : "border-gray-600 focus:border-amber-500 focus:ring-amber-500/20"
                      } focus:ring-2 focus:outline-none`}
                    >
                      <option value="">Sélectionnez un étage</option>
                      {Array.from({ length: 20 }, (_, i) => i).map((floor) => (
                        <option key={floor} value={floor} className="bg-gray-800">
                          {floor === 0 ? "Rez-de-chaussée" : `${floor}${floor === 1 ? "er" : "ème"} étage`}
                        </option>
                      ))}
                    </select>
                    {errors.etage && (
                      <p className="mt-2 text-sm text-red-400 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-2-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {errors.etage}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex items-start">
                  <div className="flex items-center h-5 mt-1">
                    <input
                      id="ascenseur"
                      name="ascenseur"
                      type="checkbox"
                      checked={form.ascenseur}
                      onChange={handleChange}
                      className="h-5 w-5 text-amber-500 bg-gray-700 border-gray-600 rounded focus:ring-amber-500 focus:ring-2"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="ascenseur" className="font-medium text-gray-300">
                      Présence d'un ascenseur
                    </label>
                    <p className="text-gray-500 text-sm mt-1">Cochez si votre immeuble est équipé d'un ascenseur</p>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="volume" className="block text-sm font-medium text-gray-300 mb-2">
                  Volume à déménager (en m³) *
                </label>
                <input
                  type="number"
                  id="volume"
                  name="volume"
                  min="1"
                  step="0.5"
                  value={form.volume}
                  onChange={handleChange}
                  className={`block w-full px-4 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-400 backdrop-blur-sm transition-all duration-200 ${
                    errors.volume
                      ? "border-red-500 focus:border-red-400 focus:ring-red-400/20"
                      : "border-gray-600 focus:border-amber-500 focus:ring-amber-500/20"
                  } focus:ring-2 focus:outline-none`}
                  placeholder="Ex: 30"
                />
                {errors.volume && (
                  <p className="mt-2 text-sm text-red-400 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-2-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.volume}
                  </p>
                )}
                <div className="mt-3 p-4 bg-gray-700/30 rounded-lg border border-gray-600">
                  <p className="text-sm text-gray-400">
                    💡 <strong className="text-amber-400">Aide à l'estimation :</strong>
                  </p>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-gray-500">
                    <span>• Studio ≈ 20m³</span>
                    <span>• T2 ≈ 30m³</span>
                    <span>• Maison ≈ 50m³</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Étape 3: Options */}
          {currentStep === 3 && (
            <div className="space-y-8">
              <h2 className="text-2xl font-bold text-white mb-2">Options complémentaires</h2>
              <p className="text-gray-400 mb-6">Personnalisez votre service de déménagement</p>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-4">Type de camion *</label>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
                  {TRUCK_TYPES.map((truck) => (
                    <label
                      key={truck.id}
                      className={`relative p-6 border rounded-xl cursor-pointer transition-all duration-200 ${
                        form.truckType === truck.id
                          ? "border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/20"
                          : "border-gray-600 bg-gray-700/30 hover:border-amber-400 hover:bg-amber-500/5"
                      } backdrop-blur-sm`}
                    >
                      <input
                        type="radio"
                        name="truckType"
                        value={truck.id}
                        checked={form.truckType === truck.id}
                        onChange={handleChange}
                        className="sr-only"
                        aria-labelledby={`truck-${truck.id}-label`}
                      />
                      <div className="flex items-start">
                        <div className="text-3xl mr-4">{truck.icon}</div>
                        <div className="flex-1">
                          <span
                            id={`truck-${truck.id}-label`}
                            className={`block text-lg font-semibold ${
                              form.truckType === truck.id ? "text-amber-400" : "text-gray-300"
                            }`}
                          >
                            {truck.label}
                          </span>
                          <span className="block text-sm text-gray-500 mt-1">{truck.capacity}</span>
                        </div>
                        {form.truckType === truck.id && (
                          <div className="text-amber-500">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                {errors.truckType && (
                  <p className="mt-3 text-sm text-red-400 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-2-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.truckType}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-4">Services complémentaires</label>
                <div className="space-y-4">
                  {Object.entries({
                    emballage: {
                      label: "Emballage professionnel",
                      icon: "📦",
                      desc: "Protection optimale de vos biens",
                    },
                    montageMeubles: {
                      label: "Montage/Démontage de meubles",
                      icon: "🔧",
                      desc: "Service complet d'assemblage",
                    },
                    nettoyage: { label: "Nettoyage après déménagement", icon: "🧹", desc: "Remise en état des locaux" },
                    cartons: { label: "Fourniture de cartons", icon: "📋", desc: "Cartons de qualité professionnelle" },
                    gardeMeubles: { label: "Garde-meubles sécurisé", icon: "🏢", desc: "Stockage temporaire sécurisé" },
                  }).map(([key, service]) => (
                    <div
                      key={key}
                      className={`p-4 border rounded-lg transition-all duration-200 ${
                        form.services && form.services[key]
                          ? "border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/10"
                          : "border-gray-600 bg-gray-700/30 hover:border-amber-400"
                      } backdrop-blur-sm`}
                    >
                      <div className="flex items-start">
                        <div className="flex items-center h-5 mt-1">
                          <input
                            id={`service-${key}`}
                            name={`services.${key}`}
                            type="checkbox"
                            checked={form.services ? form.services[key] : false}
                            onChange={handleChange}
                            className="h-5 w-5 text-amber-500 bg-gray-700 border-gray-600 rounded focus:ring-amber-500 focus:ring-2"
                          />
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="flex items-center">
                            <span className="text-xl mr-2">{service.icon}</span>
                            <label
                              htmlFor={`service-${key}`}
                              className={`font-medium ${form.services && form.services[key] ? "text-amber-400" : "text-gray-300"}`}
                            >
                              {service.label}
                            </label>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{service.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="commentaires" className="block text-sm font-medium text-gray-300 mb-2">
                  Informations complémentaires
                </label>
                <textarea
                  id="commentaires"
                  name="commentaires"
                  rows={4}
                  value={form.commentaires}
                  onChange={handleChange}
                  className="block w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 backdrop-blur-sm transition-all duration-200 focus:border-amber-500 focus:ring-amber-500/20 focus:ring-2 focus:outline-none resize-none"
                  placeholder="Précisez ici toute information utile pour votre déménagement (objets fragiles, accès difficile, contraintes particulières...)"
                />
              </div>
            </div>
          )}

          {/* Étape 4: Récapitulatif */}
          {currentStep === 4 && (
            <div className="space-y-8">
              <h2 className="text-2xl font-bold text-white mb-2">Récapitulatif de votre demande</h2>
              <p className="text-gray-400 mb-6">Vérifiez les informations avant de confirmer</p>

              <div className="bg-gray-700/30 p-6 rounded-xl border border-gray-600 backdrop-blur-sm">
                <h3 className="text-xl font-semibold text-amber-400 mb-6 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                  </svg>
                  Détails du trajet
                </h3>
                <dl className="space-y-4">
                  <div className="flex justify-between py-2 border-b border-gray-600">
                    <dt className="text-sm font-medium text-gray-400">De</dt>
                    <dd className="text-sm text-gray-300 text-right max-w-xs">
                      {form.depart || <span className="text-gray-500">Non spécifié</span>}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-600">
                    <dt className="text-sm font-medium text-gray-400">À</dt>
                    <dd className="text-sm text-gray-300 text-right max-w-xs">
                      {form.arrivee || <span className="text-gray-500">Non spécifié</span>}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-600">
                    <dt className="text-sm font-medium text-gray-400">Date prévue</dt>
                    <dd className="text-sm text-gray-300">
                      {form.date ? formatDate(form.date) : <span className="text-gray-500">Non spécifiée</span>}
                    </dd>
                  </div>
                </dl>

                <h3 className="text-xl font-semibold text-amber-400 mt-8 mb-6 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
                    />
                  </svg>
                  Détails du logement
                </h3>
                <dl className="space-y-4">
                  <div className="flex justify-between py-2 border-b border-gray-600">
                    <dt className="text-sm font-medium text-gray-400">Type de logement</dt>
                    <dd className="text-sm text-gray-300 capitalize">
                      {form.typeLogement || <span className="text-gray-500">Non spécifié</span>}
                    </dd>
                  </div>
                  {form.typeLogement === "appartement" && (
                    <div className="flex justify-between py-2 border-b border-gray-600">
                      <dt className="text-sm font-medium text-gray-400">Étage</dt>
                      <dd className="text-sm text-gray-300">
                        {form.etage === "0" ? (
                          "Rez-de-chaussée"
                        ) : form.etage ? (
                          `${form.etage}${form.etage === "1" ? "er" : "ème"} étage`
                        ) : (
                          <span className="text-gray-500">Non spécifié</span>
                        )}
                      </dd>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-b border-gray-600">
                    <dt className="text-sm font-medium text-gray-400">Volume estimé</dt>
                    <dd className="text-sm text-gray-300">
                      {form.volume ? `${form.volume} m³` : <span className="text-gray-500">Non spécifié</span>}
                    </dd>
                  </div>
                </dl>

                <h3 className="text-xl font-semibold text-amber-400 mt-8 mb-6 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                    />
                  </svg>
                  Options sélectionnées
                </h3>
                <dl className="space-y-4">
                  <div className="flex justify-between py-2 border-b border-gray-600">
                    <dt className="text-sm font-medium text-gray-400">Type de camion</dt>
                    <dd className="text-sm text-gray-300">
                      {form.truckType ? (
                        TRUCK_TYPES.find((t) => t.id === form.truckType)?.label || form.truckType
                      ) : (
                        <span className="text-gray-500">Non spécifié</span>
                      )}
                    </dd>
                  </div>
                  <div className="py-2">
                    <dt className="text-sm font-medium text-gray-400 mb-2">Services</dt>
                    <dd className="text-sm text-gray-300">
                      {form.services && typeof form.services === 'object' && Object.entries(form.services).some(([_, value]) => value) ? (
                        <div className="space-y-2">
                          {form.services.emballage && (
                            <div className="flex items-center text-green-400">
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Emballage professionnel
                            </div>
                          )}
                          {form.services.montageMeubles && (
                            <div className="flex items-center text-green-400">
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Montage/Démontage de meubles
                            </div>
                          )}
                          {form.services.nettoyage && (
                            <div className="flex items-center text-green-400">
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Nettoyage après déménagement
                            </div>
                          )}
                          {form.services.cartons && (
                            <div className="flex items-center text-green-400">
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Fourniture de cartons
                            </div>
                          )}
                          {form.services.gardeMeubles && (
                            <div className="flex items-center text-green-400">
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Garde-meubles sécurisé
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">Aucun service supplémentaire sélectionné</span>
                      )}
                    </dd>
                  </div>
                  {form.commentaires && (
                    <div className="py-2">
                      <dt className="text-sm font-medium text-gray-400 mb-2">Commentaires</dt>
                      <dd className="text-sm text-gray-300 whitespace-pre-line bg-gray-800/50 p-3 rounded-lg">
                        {form.commentaires}
                      </dd>
                    </div>
                  )}
                </dl>

                <div className="mt-8 p-6 bg-gradient-to-r from-amber-900/20 to-yellow-900/20 rounded-xl border border-amber-700/50 backdrop-blur-sm">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-8 w-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                        />
                      </svg>
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="text-lg font-semibold text-amber-400">Estimation du coût</h3>
                      <div className="mt-3">
                        <p className="text-2xl font-bold text-white">
                          Votre devis estimé : <span className="text-amber-400">{formatTND(calculateEstimate())}</span>
                        </p>
                        <p className="mt-2 text-sm text-gray-400">
                          💡 Ce montant est une estimation basée sur vos informations. Un devis définitif vous sera
                          communiqué après validation de votre demande par nos experts.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <div className="flex items-start">
                  <div className="flex items-center h-5 mt-1">
                    <input
                      id="terms"
                      name="terms"
                      type="checkbox"
                      required
                      className="h-5 w-5 text-amber-500 bg-gray-700 border-gray-600 rounded focus:ring-amber-500 focus:ring-2"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="terms" className="font-medium text-gray-300">
                      J'accepte les conditions générales de vente
                    </label>
                    <p className="text-gray-500 text-sm mt-1">
                      En cochant cette case, vous acceptez nos conditions générales de vente et notre politique de
                      confidentialité.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-gray-700 flex items-center justify-between">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={prevStep}
                className="inline-flex items-center px-6 py-3 border border-gray-600 shadow-sm text-sm font-medium rounded-lg text-gray-300 bg-gray-700/50 hover:bg-gray-600/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-200 backdrop-blur-sm"
                disabled={loading}
              >
                <svg className="mr-2 -ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Précédent
              </button>
            ) : (
              <div></div>
            )}

            <div className="flex items-center space-x-4">
              {currentStep < STEPS.length ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="inline-flex items-center px-8 py-3 border border-transparent text-sm font-semibold rounded-lg shadow-lg text-gray-900 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 hover:from-amber-500 hover:via-yellow-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-200 transform hover:scale-105"
                  disabled={loading}
                >
                  Suivant
                  <svg className="ml-2 -mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="inline-flex items-center px-8 py-3 border border-transparent text-sm font-semibold rounded-lg shadow-lg text-gray-900 bg-gradient-to-r from-green-400 via-emerald-500 to-green-600 hover:from-green-500 hover:via-emerald-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 transform hover:scale-105"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-900" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <svg className="mr-2 -ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                        />
                      </svg>
                      Envoyer le devis
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>

        <button
          onClick={() => setChatOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full shadow-2xl flex items-center justify-center text-gray-900 hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 transform hover:scale-110 z-40"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </button>

        <ChatAssistant
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          onSendMessage={(msg) => console.log("Message:", msg)}
        />
      </div>
    </div>
  )
}

// Export par défaut du composant ClientDevis
export default ClientDevis;
